<template>
  <project-settings-layout :id="id" :name="name">
    <el-card class="project-settings-description-templates">
      <template #header>
        <h3>Description Templates</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Create re-usable templates for your task descriptions. 
          Description Templates can be accessed from the <i class="ddi ddi-description in-text" />
          button in the text editor when creating a new task.
        </p>
        <div class="dd-divider" />
        <div class="row">
          <div class="col-12 templates-list">
            <div class="templates-list__header">
              <h4>Templates</h4>
              <dd-icon-button 
                :is-disabled="isSubmitting" 
                icon="plus" 
                type="circle" 
                size="medium" 
                title="Add saved template"
                @click="beginCreateTemplate()" 
              />
            </div>
            <div v-if="hasDescTemplates">
              <project-settings-description-templates-template
                v-for="descTemplate in descTemplates"
                :id="descTemplate.id"
                :key="descTemplate.id"
                :title="descTemplate.title"
                :disabled="isSubmitting"
                @edit="beginEditTemplate(descTemplate.id)"
                @remove="removeTemplate(descTemplate.id)"
              />
            </div>
            <p v-else class="templates-list__empty-message">
              There are no description templates for this project. 
              <a href="#" class="just-a-link" @click.stop.prevent="beginCreateTemplate()">Add your first</a>!
            </p>
          </div>
        </div>
        <!-- footer -->
        
        <!-- Modal -->
        <dd-modal
          v-model:visible="modalOpened"
          :title="(isEditing)? `Edit Template` : `New Template`"
          @handle-close="closeModal"
        >
          <el-form ref="upsertTemplateForm" :model="upsertingDescTemplate" :rules="rules" label-position="top">
            <!-- Title -->
            <el-form-item label="Title" prop="title">
              <el-input
                ref="savedTemplateForm_Title"
                v-model="upsertingDescTemplate.title"
                placeholder="e.g. Bug Fixes Template"
                maxlength="100"
              />
            </el-form-item>
            <!-- Template -->
            <el-form-item label="Description" prop="description">
              <editor
                v-model="upsertingDescTemplate.description"
                fixed-height
                placeholder="Write your template..."
              />
            </el-form-item>
          </el-form>
          <template #footer>
            <span>
              <dd-button type="text" @click="closeModal">Cancel</dd-button>
              <dd-button :color="ColorAssistant.ProjectColor" @click="upsertTemplate">{{ (isEditing)? `Update Template` : `Add Template` }}</dd-button>
            </span>
          </template>
        </dd-modal>
      </template>
    </el-card>
  </project-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ProjectSettingsLayout from '@/layouts/project-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import ProjectSettingsDescriptionTemplatesTemplate from './components/project-settings-description-templates-template.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import { IDescriptionTemplate } from '@/api/types/response.types'
import {
  IInternalProjectsInputCreateDescriptionTemplate,
  IInternalProjectsInputUpdateDescriptionTemplate,
} from '@/api/types/request.types'

export default defineComponent({
  name: 'ProjectSettingsDescriptionTemplates',

  components: {
    ProjectSettingsLayout,
    ProjectSettingsDescriptionTemplatesTemplate,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      editingID: null as any,
      name: '',
      descTemplates: [] as IDescriptionTemplate[],
      upsertingDescTemplate: {} as IDescriptionTemplate,
      modalOpened: false,
      rules: {
        title: [
          {
            required: true,
            trigger: 'submit',
            message: 'Title is required',
          },
        ],
        template: [
          {
            required: true,
            trigger: 'submit',
            message: 'Template is required',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Description Templates - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasDescTemplates(): boolean {
      return this.descTemplates.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.savedTemplateForm_Title.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getDescriptionTemplatesInfo()
    this.isLoading = false
  },

  methods: {
    getDescTemplate(id: number) {
      return this.descTemplates.find(dt => dt.id === id)
    },
    async getDescriptionTemplatesInfo() {
      try {
        const response = await InternalProjectsAPI.get_DescriptionTemplates(
          this.id,
        )
        this.name = response.name
        this.descTemplates = response.listDescriptionTemplates
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async upsertTemplate() {
      // @ts-ignore
      this.$refs.upsertTemplateForm.validate((valid: boolean) => {
        if (valid) {
          this.isEditing
            ? this.updateDescriptionTemplate()
            : this.createDescriptionTemplate()
          this.closeModal()
        }
      })
    },

    beginEditTemplate(id: number): void {
      this.upsertingDescTemplate = this.getDescTemplate(id)
      this.modalOpened = true
      this.editingID = id
    },

    beginCreateTemplate(): void {
      this.upsertingDescTemplate = {} as IDescriptionTemplate
      this.modalOpened = true
      this.editingID = null
    },

    async removeTemplate(id: number) {
      await this.$confirm(
        `Are you sure you want to delete this template?<br />
         This cannot be undone.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete Description Template`,
        },
      )
      await this.deleteDescriptionTemplate(id)
    },

    closeModal(): void {
      this.modalOpened = false
      // @ts-ignore
      this.$refs.upsertTemplateForm.clearValidate()
    },

    async createDescriptionTemplate() {
      try {
        this.isSubmitting = true
        const data = this
          .upsertingDescTemplate as IInternalProjectsInputCreateDescriptionTemplate
        const response = await InternalProjectsAPI.post_CreateDescriptionTemplate(
          this.id,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getDescriptionTemplatesInfo()
        this.isSubmitting = false
      }
    },

    async updateDescriptionTemplate() {
      try {
        this.isSubmitting = true
        const data = this
          .upsertingDescTemplate as IInternalProjectsInputUpdateDescriptionTemplate
        const response = await InternalProjectsAPI.put_UpdateDescriptionTemplate(
          this.id,
          this.editingID,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getDescriptionTemplatesInfo()
        this.isSubmitting = false
      }
    },

    async deleteDescriptionTemplate(id: number) {
      try {
        this.isSubmitting = true
        const response = await InternalProjectsAPI.delete_DeleteDescriptionTemplate(
          this.id,
          id,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getDescriptionTemplatesInfo()
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.project-settings-description-templates {
  .in-text {
    font-size: 14px;
    padding: 0 2px;
  }

  .templates-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }
}
</style>
